import type { Match, MatchEvent, CommentaryEntry } from "@/types/match";
import { mockMatches as seedMatches, allLeagues as mockLeagues } from "@/data/mockMatches";
import { USE_MOCK } from "./endpoints";
import { ApiError } from "./errors";
import { isSameDay, toApiDateString } from "@/utils/format";
import {
  fetchLiveFixtures,
  fetchFixturesByDate,
  fetchFixtureById,
  searchFixtures,
  fetchLeagues,
  fetchFixtureStatistics,
  fetchFixtureLineups,
  fetchFixtureEvents,
} from "./apiFootball";
import { resolveStreamSources } from "./streamResolver";

export { ApiError };

// Simulates realistic network latency + occasional failure so loading
// skeletons and error boundaries can be exercised in the mock environment.
const simulateNetwork = <T,>(data: T, failRate = 0.03): Promise<T> =>
  new Promise((resolve, reject) => {
    const delay = 350 + Math.random() * 450;
    setTimeout(() => {
      if (Math.random() < failRate) {
        reject(new ApiError("Failed to reach match data service", 503));
      } else {
        resolve(data);
      }
    }, delay);
  });

/**
 * In-memory store that stands in for a real backend in mock mode. Cloned
 * once from the static seed data so each browser session gets its own
 * evolving state. Every mock branch below reads from `store`, never from
 * `seedMatches` directly — the seed only ever (re)initializes it.
 */
let store: Match[] = structuredClone(seedMatches);

let lastTickAt = 0;
const MIN_TICK_GAP_MS = 4000; // debounce: multiple hooks poll independently
                               // (the header ticker + the page's own fetch),
                               // so guard against advancing state twice for
                               // requests that land within the same instant.

/**
 * Without this, "live" matches would sit frozen forever in mock mode — the
 * polling infrastructure would exist but nothing would ever actually
 * change. Each call to getLiveMatches acts as one simulated tick: the clock
 * advances, and there's a small chance of a goal. In real mode this whole
 * function is bypassed — API-Football's own `/fixtures/live` is already
 * genuinely live, no simulation needed.
 */
function tickLiveMatches() {
  const now = Date.now();
  if (now - lastTickAt < MIN_TICK_GAP_MS) return;
  lastTickAt = now;

  store = store.map((m) => {
    if (m.status !== "LIVE") return m;

    const minute = (m.minute ?? 0) + 1;
    if (minute >= 90) {
      return { ...m, minute: 90, status: "FULLTIME" as const };
    }

    let { homeScore, awayScore, events, commentary } = m;
    if (Math.random() < 0.06) {
      const scoringHome = Math.random() < 0.5;
      const scorer = scoringHome ? m.homeTeam : m.awayTeam;
      homeScore = scoringHome ? homeScore + 1 : homeScore;
      awayScore = !scoringHome ? awayScore + 1 : awayScore;

      const event: MatchEvent = {
        id: `e-${m.id}-${minute}-${Math.random().toString(36).slice(2, 6)}`,
        minute,
        type: "GOAL",
        teamId: scorer.id,
        playerName: scorer.shortName + " scorer",
      };
      const commentaryEntry: CommentaryEntry = {
        id: `c-${m.id}-${minute}-${Math.random().toString(36).slice(2, 6)}`,
        minute,
        text: `GOAL! ${scorer.name} find the breakthrough in the ${minute}${ordinalSuffix(minute)} minute!`,
        isKeyMoment: true,
      };
      events = [event, ...events];
      commentary = [commentaryEntry, ...commentary];
    }

    return { ...m, minute, homeScore, awayScore, events, commentary };
  });
}

function ordinalSuffix(n: number): string {
  if (n % 10 === 1 && n % 100 !== 11) return "st";
  if (n % 10 === 2 && n % 100 !== 12) return "nd";
  if (n % 10 === 3 && n % 100 !== 13) return "rd";
  return "th";
}

/**
 * Modular data layer. Every consumer (hooks/components) talks to these
 * functions only — never to `fetch`, the mock store, or apiFootball.ts
 * directly. USE_MOCK is the single switch between the two; the shape
 * returned is identical either way, so nothing downstream needs to change.
 */
export const api = {
  async getLiveMatches(): Promise<Match[]> {
    if (USE_MOCK) {
      tickLiveMatches();
      return simulateNetwork(store.filter((m) => m.status === "LIVE" || m.status === "HALFTIME"));
    }
    return fetchLiveFixtures();
  },

  /** Matches scheduled (not yet kicked off) for a given day — defaults to today. */
  async getScheduledMatches(date: Date = new Date()): Promise<Match[]> {
    if (USE_MOCK) {
      return simulateNetwork(store.filter((m) => m.status === "SCHEDULED" && isSameDay(m.kickoff, date)));
    }
    const dayFixtures = await fetchFixturesByDate(toApiDateString(date));
    return dayFixtures.filter((m) => m.status === "SCHEDULED");
  },

  /** Every fixture (any status) for a given day — API-Football's fixtures
   * endpoint is date-scoped, so this always takes a date rather than
   * returning "everything" the way the old mock-only version did. */
  async getAllMatches(date: Date = new Date()): Promise<Match[]> {
    if (USE_MOCK) {
      tickLiveMatches();
      return simulateNetwork(store.filter((m) => isSameDay(m.kickoff, date)));
    }
    return fetchFixturesByDate(toApiDateString(date));
  },

  async getMatchById(id: string): Promise<Match> {
    if (USE_MOCK) {
      tickLiveMatches();
      const match = store.find((m) => m.id === id);
      if (!match) throw new ApiError("Match not found", 404);
      return simulateNetwork(match, 0.02);
    }
    // Fetched in parallel — each piece is independent and gracefully
    // degrades to an empty/null state on its own failure (e.g. stats aren't
    // published for every competition) rather than breaking the whole page.
    const [match, streamResult, stats, lineups, eventsResult] = await Promise.all([
      fetchFixtureById(id),
      resolveStreamSources(id),
      fetchFixtureStatistics(id).catch(() => null),
      fetchFixtureLineups(id).catch(() => null),
      fetchFixtureEvents(id).catch(() => ({ events: [] as MatchEvent[], commentary: [] as CommentaryEntry[] })),
    ]);
    return {
      ...match,
      streams: streamResult.streams,
      externalWatchUrl: streamResult.watchUrl,
      stats,
      lineups,
      events: eventsResult.events,
      commentary: eventsResult.commentary,
    };
  },

  async getLeagues() {
    if (USE_MOCK) {
      return simulateNetwork(mockLeagues);
    }
    return fetchLeagues();
  },

  async search(query: string): Promise<Match[]> {
    if (USE_MOCK) {
      const q = query.trim().toLowerCase();
      if (!q) return simulateNetwork([]);
      return simulateNetwork(
        store.filter(
          (m) =>
            m.homeTeam.name.toLowerCase().includes(q) ||
            m.awayTeam.name.toLowerCase().includes(q) ||
            m.league.name.toLowerCase().includes(q) ||
            m.league.shortName.toLowerCase().includes(q)
        )
      );
    }
    if (!query.trim()) return [];
    return searchFixtures(query);
  },
};
